/**
 * The board, one agent to a line.
 *
 * The card this replaces was measured on the deployment and it did not work.
 * Sixteen facts on each of eleven cards is a hundred and seventy-six things on
 * one screen; the card's face was `--k-surface` on a panel of `--k-surface`, a
 * contrast ratio of 1.00:1, so eleven boxes were delimited by nothing but a
 * hairline; the grid let each card find its own height, which came out between
 * 157px and 207px *inside a single row*; and because IDLE was toned `warn`,
 * eight of the eleven wore the same amber. Colour that is on three quarters of
 * a screen is not a signal, and a card that is the colour of its container is
 * not a card.
 *
 * A line fixes all four at once, and it is what a fleet of a dozen similar
 * things wants: identical rows, aligned columns, one height, no holes. What is
 * on it is what the decision needs and nothing else —
 *
 *   1. **the light** — coloured only when the state is not the resting one.
 *   2. **who** — the project, which on this fleet is one agent's whole identity.
 *   3. **what it is doing** — a word, and only where there is something to say.
 *      Working is a word as well as a pulse, because `prefers-reduced-motion`
 *      flattens animation and must not flatten meaning (CLAUDE.md).
 *   4. **what it said** — one clipped line of the last thing the agent wrote.
 *      Still the only fact on this screen that is not derivable from somewhere
 *      else in the console, so it gets the elastic column.
 *   5. **how long it has been quiet** — mono, tabular, and the thing the list is
 *      sorted by.
 *
 * The rest — the tmux handle, the account, the model, the host, the branch —
 * left the paint and kept their meaning: the search still matches them, they are
 * on the row's tooltip, and `/live`'s inspector and `/projects` hold them in
 * full. Four columns printing the same three strings on every row is what this
 * board was rebuilt to stop doing, and putting them in a card footer instead of
 * a table column did not stop it.
 *
 * Two shapes of the same row. `FleetRow` is the line. `AttentionCard` is what an
 * agent that is *asking* for a person gets: the same identity, then the reply
 * editor, in a block that is visibly a different object from the list under it.
 */

import { Link } from 'react-router-dom'
import clsx from 'clsx'
import {
  CornerDownLeft,
  KeyRound,
  RotateCcw,
  Square,
  Terminal,
  Trash2,
} from 'lucide-react'

import { Icon } from '@/components/icon'
import { Badge, StatusDot, type Tone } from '@/components/primitives'
import { OverflowItem, OverflowMenu } from '@/components/menu'
import { useLocale, useT } from '@/i18n/context'
import { formatDuration, formatRelative } from '@/i18n'
import { reportsSilence, silenceOf, type Silence } from './dashboardModel'
import { sessionActivityTone } from '@/lib/tone'
import { activityLabelKey, sessionHasEnded, type SessionActivity } from '@/lib/sessionActivity'
import { sessionLabel, type LastTurn, type SessionRow } from '@/terminal/wall'
import { turnSegments } from '@/lib/turnText'
import type { ProjectReading } from './projectsModel'
import { NextStep } from '@/terminal/nextStep'
import type { ID } from '@/types/domain'

/**
 * The reason a silence is worth what it is worth.
 *
 * In a `title` rather than on the row because it is an elaboration and not the
 * fact: the duration is the fact, and it is legible without it. Switched rather
 * than interpolated into a key, so the i18n contract stays a compile-time one.
 */
function whyKey(from: Silence['from']) {
  switch (from) {
    case 'done':
      return 'dashboard.silence.from.done' as const
    case 'waiting':
      return 'dashboard.silence.from.waiting' as const
    case 'busy':
      return 'dashboard.silence.from.busy' as const
    case 'transcript':
      return 'dashboard.silence.from.transcript' as const
  }
}

/** How long this agent has been quiet — the column the fleet list is sorted by. */
function Quiet({ silence }: { silence?: Silence }) {
  const t = useT()
  const { locale } = useLocale()
  // Applicable, and nothing measured it. Not "0 dakika": a session this control
  // plane has never had a view of has not thereby been quiet for no time at all.
  if (!silence) {
    return (
      <span className="k-board__none" title={t('dashboard.silence.unmeasured')}>
        —
      </span>
    )
  }
  const duration = formatDuration(silence.ms, locale)
  return (
    /* The column has no visible header, so the duration is labelled where it is
       read rather than where it is drawn: on screen, "4dk" under a heading that
       says "8 sessiz" is unambiguous; heard on its own it is a bare number.
       `aria-label` replaces the text for a screen reader, which is exactly what
       is wanted here — "4 dakikadır sessiz", not "4 dakika". */
    <span
      className="k-mono"
      title={t(whyKey(silence.from))}
      aria-label={t('dashboard.silence.for', { duration })}
    >
      {duration}
    </span>
  )
}

/**
 * The last thing the agent said, flattened to one line.
 *
 * Same precedence the detail panel uses — the reply, else the tool it is in the
 * middle of, else whatever the last line was — so the row and the editor under
 * it cannot disagree about what is being replied to. Newlines collapse, because
 * this is a single line of a grid cell and a stray `\n` would otherwise decide
 * where the clip lands.
 *
 * `harness` comes back with it: `<task-notification>` and its kind are the
 * harness waking the agent, not something a person wrote, and on the old board
 * they were the headline of five cards out of eleven. Shown — an empty cell
 * would be a worse lie than an honest "the harness poked it and nothing has come
 * back" — but in mono and muted, which is this console's whole vocabulary for
 * "machine, not human" (CLAUDE.md).
 */
export function saidOf(turn?: LastTurn): { text: string; harness: boolean } | undefined {
  if (!turn) return undefined
  const oneLine = (raw: string | undefined) => {
    const read = turnSegments(raw)
    const text = read.segments
      .map((s) => s.text)
      .join('')
      .replace(/\s+/g, ' ')
      .trim()
    return text ? { text, harness: read.harness } : undefined
  }
  return (
    oneLine(turn.reply) ??
    (turn.tool ? { text: turn.tool, harness: false } : undefined) ??
    oneLine(turn.lastLine)
  )
}

/**
 * The exit status, when there is one and it is not zero.
 *
 * The single most diagnostic fact about a session that failed, and the board had
 * nowhere for it: a FAILED row read "Sorun var", the last command it happened to
 * be running, and how long ago — which is the shape of the problem without any
 * of the answer. It matters more here than anywhere because these CLIs draw on
 * the alternate screen and restore what was under them as they exit, so the pane
 * an operator opens next shows the frame from *before* the failure
 * (`dead-pane-shows-the-wrong-frame`). The status is what survives.
 *
 * A zero is not shown. A session that ended cleanly has nothing to explain, and
 * "çıkış 0" beside a normal ending is the invented measurement this console
 * refuses.
 */
function exitOf(session: SessionRow, t: ReturnType<typeof useT>): string | undefined {
  if (!sessionHasEnded(session.state)) return undefined
  const code = session.exitStatus
  if (code == null || code === 0) return undefined
  return t('live.exitStatus', { code: String(code) })
}

export interface SessionActs {
  open: (id: ID) => void
  restart: (session: SessionRow) => void
  switchAccount: (id: ID) => void
  stop: (session: SessionRow) => void
}

/** The four acts a session has, in the one implementation both shapes use. */
function RowMenu({ session, acts }: { session: SessionRow; acts: SessionActs }) {
  const t = useT()
  const managed = session.kind === 'MANAGED'
  const ended = sessionHasEnded(session.state)
  const switchable = managed && Boolean(session.safeBoundary)
  return (
    <OverflowMenu label={t('common.actions')}>
      <OverflowItem onClick={() => acts.open(session.id)}>
        <Icon icon={Terminal} size={14} /> {t('live.open')}
      </OverflowItem>
      {managed && (
        <OverflowItem onClick={() => acts.restart(session)}>
          <Icon icon={RotateCcw} size={14} /> {t('live.restart')}
        </OverflowItem>
      )}
      {managed && (
        <OverflowItem
          onClick={() => acts.switchAccount(session.id)}
          disabled={!switchable}
          title={switchable ? undefined : t('live.switchAccountBusy')}
        >
          <Icon icon={KeyRound} size={14} /> {t('live.switchAccount')}
        </OverflowItem>
      )}
      {managed && (
        <OverflowItem onClick={() => acts.stop(session)}>
          <Icon icon={ended ? Trash2 : Square} size={14} />{' '}
          {t(ended ? 'live.clear' : 'live.stop')}
        </OverflowItem>
      )}
    </OverflowMenu>
  )
}

/**
 * The three acts a row offers, in the one implementation both shapes use.
 *
 * Measured on the previous board, the two shapes disagreed about everything:
 * eight distinct right edges for buttons on one screen, a text button here and a
 * 26px icon there for the *same* act, and "open the terminal" reachable from a
 * fleet row only by clicking the row — an affordance with nothing to see. Same
 * three controls, same size, same order, same column, in both sections.
 */
function RowActs({
  session,
  acts,
  drivable,
  open,
  onToggle,
}: {
  session: SessionRow
  acts: SessionActs
  drivable: boolean
  open: boolean
  onToggle: () => void
}) {
  const t = useT()
  return (
    <div className="k-board__acts">
      {drivable && (
        <button
          type="button"
          className="k-board__act"
          aria-expanded={open}
          aria-label={t('chief.next')}
          title={t('chief.next')}
          onClick={onToggle}
        >
          <Icon icon={CornerDownLeft} size={14} />
        </button>
      )}
      <button
        type="button"
        className="k-board__act"
        aria-label={t('live.open')}
        title={t('live.open')}
        onClick={() => acts.open(session.id)}
      >
        <Icon icon={Terminal} size={14} />
      </button>
      <RowMenu session={session} acts={acts} />
    </div>
  )
}

/**
 * The facts that left the paint, on the row's tooltip.
 *
 * Not the only way to them — `/live`'s inspector carries all of these and more,
 * `/projects` holds the branch, and the search field above the list still
 * matches every one of them, so a session is findable by the account or the
 * machine it runs on without any of it costing a pixel. This is the convenience,
 * not the record.
 */
function identityTitle(
  parts: Array<string | undefined>,
): string | undefined {
  const kept = parts.filter((p): p is string => Boolean(p))
  return kept.length > 0 ? kept.join(' · ') : undefined
}

interface RowProps {
  session: SessionRow
  activity: SessionActivity
  acts: SessionActs
  /** Whether the reply editor is showing. */
  open: boolean
  /** Whether it has ever been opened. A control stays mounted once it has, so
   *  collapsing a row parks a typed draft rather than throwing it away — the
   *  prefill rule's other half (CLAUDE.md): no interaction may un-remember what
   *  somebody set. */
  mounted: boolean
  autoFocus: boolean
  onToggle: () => void
  onDismiss: () => void
  onSent: () => void
  /** How long this browser has held the payload the row came from — added to the
   *  server's measurement rather than differencing two clocks. */
  heldMs: number
  accountLabel?: string
  modelLabel?: string
  hostLabel?: string
  project?: ProjectReading
}

/** The reply editor, in the one place both shapes mount it. */
function Editor({
  session,
  open,
  mounted,
  autoFocus,
  onDismiss,
  onSent,
}: Pick<RowProps, 'session' | 'open' | 'mounted' | 'autoFocus' | 'onDismiss' | 'onSent'>) {
  if (!mounted) return null
  return (
    <div className="k-board__editor" hidden={!open}>
      <NextStep
        sessionId={session.id}
        turn={session.lastTurn}
        autoFocus={autoFocus}
        onSent={onSent}
        onDismiss={onDismiss}
      />
    </div>
  )
}

/* --------------------------------------------------------------- The line */

export function FleetRow({
  session,
  activity,
  acts,
  open,
  mounted,
  autoFocus,
  onToggle,
  onDismiss,
  onSent,
  heldMs,
  accountLabel,
  modelLabel,
  hostLabel,
  project,
}: RowProps) {
  const t = useT()
  const { locale } = useLocale()
  const tone: Tone = sessionActivityTone(activity)
  const ended = sessionHasEnded(session.state)
  // Only a live session Kimene can type into has a next step at all; an observed
  // one is watched, not driven.
  const drivable = !ended && session.kind !== 'OBSERVED'
  const label = sessionLabel(session, session.id)
  const said = saidOf(session.lastTurn)
  const exit = exitOf(session, t)
  const handle =
    session.tmuxSession && session.tmuxSession !== label ? session.tmuxSession : undefined

  /* The word, where there is a word worth printing.
   *
   * IDLE is the resting state of this fleet — eight of eleven at any moment —
   * and the column beside it already reports exactly how long it has been in it,
   * as a duration. "Boşta · 4dk" is the same fact twice, and it is the sentence
   * that made the old board read as though the whole fleet were stalled. Empty
   * on purpose. */
  const word = activity === 'IDLE' ? null : t(activityLabelKey(activity))

  return (
    <li className={clsx('k-board__item', open && 'k-board__item--open')}>
      {/* No tone class on the row itself. A coloured leading edge repeating what
          the dot already says is a second copy of one signal, and this screen's
          whole problem was that everything on it was equally loud. */}
      <Link to={`/live/${session.id}`} className="k-board__row">
        {/* Wrapped so the cell can be placed rather than auto-flowed: on the
            phone layout the state column is hidden, and a grid where one child
            auto-places is a grid where hiding a cell slides the rest one track
            to the left. */}
        <span className="k-board__dot">
          <StatusDot
            tone={tone}
            live={activity === 'WORKING'}
            label={`${t(activityLabelKey(activity))} · ${session.state}${
              session.kind === 'MANAGED' ? '' : ` · ${session.kind}`
            }`}
          />
        </span>
        <span
          className="k-board__name"
          title={identityTitle([
            handle,
            project?.branches[0],
            accountLabel,
            modelLabel,
            hostLabel,
          ])}
        >
          {label}
        </span>
        {/* No kind badge here. It only ever appears on a session Kimene did not
            start, and "Yalnızca izlenen" wants 116px of a cell that also has to
            hold the activity — measured, it left the word as "Ö..", which is
            worse than not saying it. Three signals already separate such a row:
            an unmeasured session takes the unknown hue rather than a quiet grey
            (CLAUDE.md), the light's own label names the kind, and an OBSERVED
            session is offered no reply control because it is watched, not
            driven. */}
        <span className={clsx('k-board__word', `k-board__word--${tone}`)}>
          {word && <span className="k-board__wordtext">{word}</span>}
        </span>
        <span
          className={clsx('k-board__said', said?.harness && 'k-board__said--harness k-mono')}
          title={said?.text}
        >
          {exit && <span className="k-board__exit k-mono">{exit}</span>}
          {said ? (
            said.text
          ) : (
            !exit && (
              <span className="k-board__none" title={t('session.lastTurn.unmeasured')}>
                —
              </span>
            )
          )}
        </span>
        <span className="k-board__quiet">
          {ended ? (
            <span className="k-mono" title={t('dashboard.row.endedWhy')}>
              {session.endedAt ? formatRelative(session.endedAt, locale) : '—'}
            </span>
          ) : reportsSilence(session, activity) ? (
            <Quiet silence={silenceOf(session, heldMs)} />
          ) : null}
        </span>
      </Link>
      {/* Beside the link rather than inside it: a button nested in an anchor is
          neither valid nor operable, and the cockpit's list makes the same split
          for the same reason. */}
      <RowActs
        session={session}
        acts={acts}
        drivable={drivable}
        open={open}
        onToggle={onToggle}
      />
      {drivable && (
        <Editor
          session={session}
          open={open}
          mounted={mounted}
          autoFocus={autoFocus}
          onDismiss={onDismiss}
          onSent={onSent}
        />
      )}
    </li>
  )
}

/* ----------------------------------------------------------- Asking for me */

/**
 * An agent that wants a person, given the room to be answered.
 *
 * On `--k-surface-raised` with its own border, because this is the one thing on
 * the screen that is not a member of the list: the previous board ranked it
 * first among eleven identical cards, and ranking is not separating — an
 * operator still had to read all eleven to find out that only one of them was
 * asking.
 */
export function AttentionCard({
  session,
  activity,
  acts,
  open,
  mounted,
  autoFocus,
  onToggle,
  onDismiss,
  onSent,
  heldMs,
  accountLabel,
  modelLabel,
  hostLabel,
  project,
}: RowProps) {
  const t = useT()
  const tone: Tone = sessionActivityTone(activity)
  const drivable = !sessionHasEnded(session.state) && session.kind !== 'OBSERVED'
  const label = sessionLabel(session, session.id)
  const said = saidOf(session.lastTurn)
  const exit = exitOf(session, t)
  const handle =
    session.tmuxSession && session.tmuxSession !== label ? session.tmuxSession : undefined

  return (
    /* The same six columns the fleet list uses, so the name, the state, the
       agent's words, the duration and the controls sit on the *same* vertical
       rules in both sections. They did not before: the names were 11px apart,
       the words started at x=51 here and x=290 there, and the duration floated
       on `margin-inline-start: auto`, so it moved 167px between two cards
       depending on which buttons happened to follow it. */
    <li className={clsx('k-board__ask', `k-board__ask--${tone}`, open && 'k-board__ask--open')}>
      <span className="k-board__dot">
        <StatusDot
          tone={tone}
          live={activity === 'WORKING'}
          label={`${t(activityLabelKey(activity))} · ${session.state}`}
        />
      </span>
      <Link
        to={`/live/${session.id}`}
        className="k-board__askname"
        title={identityTitle([handle, project?.branches[0], accountLabel, modelLabel, hostLabel])}
      >
        {label}
      </Link>
      <span className="k-board__word">
        <Badge tone={tone} subtle>
          {t(activityLabelKey(activity))}
        </Badge>
      </span>
      {/* Collapsed, the same one clipped line the fleet rows carry, in the same
          column. Open, the editor quotes the reply whole, so printing it here as
          well would be the same sentence twice sixty pixels apart. */}
      <span className={clsx('k-board__said', said?.harness && 'k-mono')}>
        {open ? null : (
          <>
            {exit && <span className="k-board__exit k-mono">{exit}</span>}
            {said?.text ?? ''}
          </>
        )}
      </span>
      <span className="k-board__quiet">
        <Quiet silence={silenceOf(session, heldMs)} />
      </span>
      <RowActs
        session={session}
        acts={acts}
        drivable={drivable}
        open={open}
        onToggle={onToggle}
      />
      {drivable && (
        <Editor
          session={session}
          open={open}
          mounted={mounted}
          autoFocus={autoFocus}
          onDismiss={onDismiss}
          onSent={onSent}
        />
      )}
    </li>
  )
}
